import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api/client.js';
import type { AssignableApp, TeamUser } from '../api/types.js';
import { useApp, useDocumentTitle, useMe } from '../app/context.js';
import { useToast } from '../components/toast.js';
import { RoleBadges, RoleEditor, Sheet, StatusBadge, TENANT_ADMIN, dateDe } from '../components/ui.js';

/**
 * Team des eigenen Betriebs (nur Admin des Betriebs): einladen, Rollen je Tool, entfernen.
 * Admins des Betriebs legt allein die Betriebsam-Administration fest (Backend erzwingt das).
 */
export function TeamPage() {
  useDocumentTitle('Team');
  const { api } = useApp();
  const me = useMe().data!;
  const team = useQuery({ queryKey: ['team'], queryFn: () => api.team(), enabled: me.tenantAdmin });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamUser | null>(null);

  if (!me.tenantAdmin) return <Navigate to="/" replace />;
  const apps = team.data?.apps ?? [];

  return (
    <main className="page">
      <header className="page__head">
        <div className="page__head--stack">
          <span className="eyebrow">{me.tenant?.name}</span>
          <h1 className="page__title">Team</h1>
        </div>
        <button className="btn btn--primary" onClick={() => setInviting(true)}>+ Einladen</button>
      </header>

      {team.isLoading && <div className="spinner">Lädt …</div>}
      {team.error && <div className="alert alert--error">{(team.error as Error).message}</div>}

      <section className="list" aria-label="Mitglieder">
        {team.data?.users.map((u) => (
          <article key={u.userId} className="list__item">
            <div className="list__main">
              <strong>
                {u.name}
                {u.userId === me.userId && <span className="muted"> (du)</span>}
              </strong>
              <span className="muted">{u.email}</span>
              <RoleBadges roles={u.roles} apps={apps} />
            </div>
            <div className="list__side">
              <StatusBadge status={u.status} />
              <button className="btn btn--ghost btn--sm" onClick={() => setEditing(u)}>Bearbeiten</button>
            </div>
          </article>
        ))}
      </section>

      <p className="note">
        Rollen gelten für die freigeschalteten Tools deines Betriebs. Geänderte Rechte wirken spätestens bei der nächsten Anmeldung im Tool.
        Wer Admin des Betriebs ist, legt das Betriebsam-Team fest – dafür wende dich bitte an uns.
      </p>

      {inviting && <InviteSheet apps={apps} onClose={() => setInviting(false)} />}
      {editing && <EditSheet user={editing} apps={apps} isSelf={editing.userId === me.userId} onClose={() => setEditing(null)} />}
    </main>
  );
}

function InviteSheet({ apps, onClose }: { apps: AssignableApp[]; onClose: () => void }) {
  const { api } = useApp();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const invite = useMutation({
    mutationFn: () => api.invite({ name: name.trim(), email: email.trim(), roles }),
    onSuccess: async (u) => {
      await qc.invalidateQueries({ queryKey: ['team'] });
      toast(`Einladung an ${u.email} verschickt.`);
      onClose();
    },
  });
  const valid = name.trim() && /^\S+@\S+\.\S+$/.test(email.trim());

  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) invite.mutate();
  }

  return (
    <Sheet title="Person einladen" onClose={onClose}>
      <form onSubmit={submit} className="form">
        <div className="field">
          <label className="field__label" htmlFor="inv-name">Name</label>
          <input id="inv-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="inv-email">E-Mail</label>
          <input id="inv-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          <span className="field__hint">Die Person bekommt eine E-Mail mit vorläufigem Passwort.</span>
        </div>
        <RoleEditor apps={apps} value={roles} onChange={setRoles} idPrefix="inv" />
        {invite.error && <div className="alert alert--error">{(invite.error as ApiError).message}</div>}
        <div className="grid-2">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn--primary" disabled={!valid || invite.isPending}>Einladen</button>
        </div>
      </form>
    </Sheet>
  );
}

function EditSheet({ user, apps, isSelf, onClose }: { user: TeamUser; apps: AssignableApp[]; isSelf: boolean; onClose: () => void }) {
  const { api } = useApp();
  const qc = useQueryClient();
  const toast = useToast();
  const [roles, setRoles] = useState(user.roles);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['team'] }), qc.invalidateQueries({ queryKey: ['me'] })]);

  const save = useMutation({
    mutationFn: () => api.updateMember(user.userId, { roles }),
    onSuccess: async () => {
      await refresh();
      toast('Rollen gespeichert.');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.removeMember(user.userId),
    onSuccess: async () => {
      await refresh();
      toast(`${user.name} wurde entfernt.`);
      onClose();
    },
  });
  const resend = useMutation({
    mutationFn: () => api.resendInvitation(user.userId),
    onSuccess: () => toast(`Einladung erneut an ${user.email} verschickt.`),
  });
  const changed = [...roles].sort().join() !== [...user.roles].sort().join();
  const isAdmin = user.roles.includes(TENANT_ADMIN);
  const err = (save.error ?? remove.error ?? resend.error) as ApiError | null;

  return (
    <Sheet title={user.name} onClose={onClose}>
      <span className="muted">
        {user.email} · eingeladen am {dateDe(user.invitedAt)}
      </span>
      <RoleEditor apps={apps} value={roles} onChange={setRoles} idPrefix="edit" />
      {err && <div className="alert alert--error">{err.message}</div>}
      <div className="grid-2">
        <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
        <button type="button" className="btn btn--primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}>Speichern</button>
      </div>
      <div className="sheet__footer">
        {user.status === 'invited' && (
          <button type="button" className="link-btn" disabled={resend.isPending} onClick={() => resend.mutate()}>
            Einladung erneut senden
          </button>
        )}
        {isAdmin && !isSelf && <p className="note">Admins des Betriebs kann nur das Betriebsam-Team entfernen.</p>}
        {!isSelf &&
          !isAdmin &&
          (confirmRemove ? (
            <div className="alert alert--warn" style={{ flexDirection: 'column' }}>
              <strong>{user.name} wirklich entfernen?</strong>
              <span>Das Konto wird gelöscht. Daten in den Tools bleiben nach deren Aufbewahrungsregeln erhalten.</span>
              <div className="grid-2">
                <button type="button" className="btn btn--ghost" onClick={() => setConfirmRemove(false)}>Nein</button>
                <button type="button" className="btn btn--primary" disabled={remove.isPending} onClick={() => remove.mutate()}>Ja, entfernen</button>
              </div>
            </div>
          ) : (
            <button type="button" className="link-btn link-btn--danger" onClick={() => setConfirmRemove(true)}>
              Aus dem Team entfernen
            </button>
          ))}
      </div>
    </Sheet>
  );
}
